import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronLeft, Calendar, ArrowUpRight } from "lucide-react";

import { fetchBlogPost, fetchRelatedPosts } from "@/lib/api";

const SOURCE_LABEL = {
  branch_city: "Zelfde check + stad",
  branch: "Zelfde check",
  category: "Zelfde rubriek",
  recent: "Recent",
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    setPost(null);
    setRelated([]);
    fetchBlogPost(slug)
      .then(setPost)
      .catch(() => setError("Blog-post niet gevonden"));
    fetchRelatedPosts(slug, 4).then(setRelated).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-bold text-ink">{error}</h1>
        <Link to="/blog" className="inline-block mt-6 text-[#002FA7] font-semibold">← Terug naar blog</Link>
      </div>
    );
  }
  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20">
        <div className="h-8 w-1/2 bg-slate-200 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <article className="py-16" data-testid={`blog-post-${slug}`}>
      <Helmet>
        <title>{`${post.title} — CheckExpert blog`}</title>
        <meta name="description" content={post.meta_description || post.excerpt || ""} />
        <link rel="canonical" href={`https://checkexpert.be/blog/${post.slug}`} />
      </Helmet>
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-ink-soft hover:text-ink mb-6">
          <ChevronLeft className="h-4 w-4" /> Alle blog-posts
        </Link>
        <div className="flex items-center gap-2 text-xs text-ink-soft">
          <Calendar className="h-3.5 w-3.5" />
          {post.published_at?.slice(0, 10)} · {post.author || "CheckExpert"}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-ink mt-3 tracking-tight">{post.title}</h1>
        <p className="text-ink-soft text-lg mt-5">{post.excerpt}</p>
        {post.image_url && (
          <img src={post.image_url} alt={post.title} className="w-full h-72 object-cover rounded-2xl mt-8" />
        )}
        <div
          className="mt-8 prose prose-slate max-w-none text-ink leading-relaxed"
          dangerouslySetInnerHTML={{ __html: post.body_html || "" }}
        />

        {related.length > 0 && (
          <section className="mt-16 border-t border-border pt-10" data-testid="related-posts">
            <h2 className="font-display text-2xl font-semibold text-ink tracking-tight">Verder lezen</h2>
            <p className="text-sm text-ink-soft mt-1">Andere artikels die je nu meteen kan oppikken.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="group rounded-2xl border border-border bg-white p-5 hover:border-ink-soft transition-colors"
                  data-testid={`related-post-${r.slug}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#002FA7]">
                      {SOURCE_LABEL[r.match_source] || "Verwant"}
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-ink-soft group-hover:text-ink" />
                  </div>
                  <h3 className="mt-3 font-display text-lg font-semibold text-ink leading-snug line-clamp-2">{r.title}</h3>
                  {r.excerpt && (
                    <p className="mt-2 text-sm text-ink-soft line-clamp-2">{r.excerpt}</p>
                  )}
                  <div className="mt-3 text-xs text-ink-subtle">
                    {r.published_at?.slice(0, 10)}
                    {r.topic_city ? ` · ${r.topic_city}` : ""}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}
